// SMS service for booking confirmations

// Twilio configuration from environment variables
const TWILIO_ACCOUNT_SID = process.env.REACT_APP_TWILIO_ACCOUNT_SID || 'your_account_sid';
const TWILIO_AUTH_TOKEN = process.env.REACT_APP_TWILIO_AUTH_TOKEN || 'your_auth_token';
const TWILIO_PHONE_NUMBER = process.env.REACT_APP_TWILIO_PHONE_NUMBER || '+1234567890';

// Initialize Twilio client (Note: This should ideally be done on backend for security)
let twilioClient = null;

// Alternative SMS service using a backend API (recommended approach)
export async function sendBookingConfirmationSMS(bookingDetails) {
  try {
    console.log('📱 Attempting to send booking confirmation SMS...');
    console.log('📱 SMS details:', {
      guestPhone: bookingDetails.guestPhone,
      guestName: bookingDetails.guestName,
      bookingId: bookingDetails.id
    });

    // Validate required fields
    if (!bookingDetails.guestPhone) {
      throw new Error('Guest phone number is required but missing');
    }

    // Format phone number (ensure it starts with country code)
    const formattedPhone = formatPhoneNumber(bookingDetails.guestPhone);
    
    // Create SMS message
    const smsMessage = generateSMSMessage(bookingDetails);
    
    console.log('📱 Formatted phone:', formattedPhone);
    console.log('📱 SMS message:', smsMessage);

    // Option 1: Use backend API (recommended)
    const smsResult = await sendSMSViaBackend(formattedPhone, smsMessage);
    
    if (smsResult.success) {
      console.log('✅ Booking confirmation SMS sent successfully');
      return {
        success: true,
        messageId: smsResult.messageId,
        message: 'Booking confirmation SMS sent successfully'
      };
    } else {
      throw new Error(smsResult.error || 'SMS sending failed');
    }

  } catch (error) {
    console.error('❌ Failed to send booking confirmation SMS:', error);
    console.error('❌ SMS Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    return {
      success: false,
      error: error.message || 'Failed to send SMS'
    };
  }
}

// Format phone number to international format
function formatPhoneNumber(phone) {
  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');
  
  // If it starts with 91 (India), add +
  if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
    return '+' + cleanPhone;
  }
  
  // If it's 10 digits, assume Indian number and add +91
  if (cleanPhone.length === 10) {
    return '+91' + cleanPhone;
  }
  
  // If it doesn't start with +, add it
  if (!phone.startsWith('+')) {
    return '+' + cleanPhone;
  }
  
  return phone;
}

// Generate SMS message content
function generateSMSMessage(bookingDetails) {
  return `🏠 Skyline Backpackers - Booking Confirmed!

Booking ID: ${bookingDetails.id}
Guest: ${bookingDetails.guestName}
Check-in: ${bookingDetails.checkIn}
Check-out: ${bookingDetails.checkOut}
Beds: ${bookingDetails.beds}
Total: ₹${bookingDetails.total || bookingDetails.amount}

Address: Sindhi Colony, Hyderabad
Contact: +91 90327 97797

Thank you for choosing us!`;
}

// Send SMS via backend API (recommended approach)
async function sendSMSViaBackend(phone, message) {
  try {
    // This would call your backend API endpoint
    const response = await fetch('/api/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: phone,
        message: message,
        from: 'Skyline Backpackers'
      })
    });

    if (response.ok) {
      const result = await response.json();
      return { 
        success: true, 
        messageId: result.messageId,
        message: 'SMS sent via backend service' 
      };
    } else {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Backend SMS service failed');
    }

  } catch (error) {
    console.error('❌ Backend SMS service failed:', error);
    
    // Fallback to alternative SMS service
    return await sendSMSViaAlternativeService(phone, message);
  }
}

// Alternative SMS service (fallback)
async function sendSMSViaAlternativeService(phone, message) {
  try {
    // Alternative 1: Use SMS gateway service like Fast2SMS, TextLocal, etc.
    // This is a placeholder - you would integrate with your preferred SMS service
    
    console.log('📱 Using alternative SMS service...');
    
    // Example with Fast2SMS (Indian SMS service)
    const fast2smsResult = await sendViaFast2SMS(phone, message);
    if (fast2smsResult.success) {
      return fast2smsResult;
    }
    
    // If all services fail, return error
    throw new Error('All SMS services failed');
    
  } catch (error) {
    console.error('❌ Alternative SMS service failed:', error);
    return {
      success: false,
      error: 'All SMS services unavailable'
    };
  }
}

// Fast2SMS integration (example for Indian SMS service)
async function sendViaFast2SMS(phone, message) {
  try {
    const FAST2SMS_API_KEY = process.env.REACT_APP_FAST2SMS_API_KEY;
    
    if (!FAST2SMS_API_KEY || FAST2SMS_API_KEY === 'your_fast2sms_api_key') {
      throw new Error('Fast2SMS API key not configured');
    }
    
    const response = await fetch('https://www.fast2sms.com/dev/bulkV2', {
      method: 'POST',
      headers: {
        'Authorization': FAST2SMS_API_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        route: 'v3',
        sender_id: 'SKYLIN',
        message: message,
        language: 'english',
        flash: 0,
        numbers: phone.replace('+91', '') // Remove +91 for Fast2SMS
      })
    });

    if (response.ok) {
      const result = await response.json();
      if (result.return === true) {
        return {
          success: true,
          messageId: result.request_id,
          message: 'SMS sent via Fast2SMS'
        };
      } else {
        throw new Error(result.message || 'Fast2SMS failed');
      }
    } else {
      throw new Error('Fast2SMS API request failed');
    }

  } catch (error) {
    console.error('❌ Fast2SMS failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

// Send admin SMS notification
export async function sendAdminSMSNotification(bookingDetails) {
  try {
    const adminPhone = '+919032797797'; // Admin phone number
    
    const adminMessage = `🚨 New Booking Alert!

ID: ${bookingDetails.id}
Guest: ${bookingDetails.guestName}
Phone: ${bookingDetails.guestPhone}
Check-in: ${bookingDetails.checkIn}
Beds: ${bookingDetails.beds}
Amount: ₹${bookingDetails.total || bookingDetails.amount}

Login to admin panel for details.`;

    const result = await sendSMSViaBackend(adminPhone, adminMessage);
    
    if (result.success) {
      console.log('✅ Admin SMS notification sent successfully');
    } else {
      console.log('❌ Admin SMS notification failed:', result.error);
    }
    
    return result;

  } catch (error) {
    console.error('❌ Failed to send admin SMS notification:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

// Test SMS functionality
export async function testSMSService(testPhone = '+919876543210') {
  try {
    console.log('📱 Testing SMS service...');
    
    const testBooking = {
      id: 'TEST-' + Date.now(),
      guestName: 'Test User',
      guestPhone: testPhone,
      checkIn: new Date().toLocaleDateString(),
      checkOut: new Date(Date.now() + 86400000).toLocaleDateString(),
      beds: 1,
      total: 599
    };
    
    const result = await sendBookingConfirmationSMS(testBooking);
    
    if (result.success) {
      console.log('✅ SMS service test successful');
    } else {
      console.log('❌ SMS service test failed:', result.error);
    }
    
    return result;
    
  } catch (error) {
    console.error('❌ SMS service test failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
}
